import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ALL_ARTICLES = [
    {
        id: '1',
        title: '5 Common Mistakes Businesses Make When Adopting AI',
        date: 'September 18, 2025',
        image: 'https://framerusercontent.com/images/cP1GNLLWaB10MRp9jKnjWjsZA.jpg?width=669&height=450',
        author: {
            name: 'Sarah Mitchell',
            role: 'AI Strategy Consultant',
            avatar: 'https://framerusercontent.com/images/dE1M3PnWabeUQOIzkbxSYm9EjvE.jpg?width=63&height=63',
        },
        subtitle: "We believe the best systems stay out of the way. That's why we keep humans in the loop, prioritize defaults over mandates, and report outcomes that leaders care about.",
        content: [
            { type: 'intro', text: 'Artificial intelligence promises faster operations, smarter insights, and new opportunities. But when companies rush into adoption, projects often stall or fail quietly.' },
            { type: 'p', text: "Let's look at the five most common mistakes and how to avoid them. The issue is rarely the algorithm itself—it's how the technology is framed, introduced, and measured." },
            { type: 'h2', text: '1. Starting with a tool instead of a problem' },
            { type: 'h3', text: 'Quick diagnostic' },
            { type: 'p', text: "If the team talks more about vendors than the job-to-be-done, you're starting from the wrong end. Ask a frontline person to describe the single most annoying step in their day—if they can't name it in one sentence, the scope is still fuzzy." },
            { type: 'list', items: ['Litmus test: can you say which manual step disappears on day one?', 'If not, tighten the brief before choosing a platform.'] },
            { type: 'h3', text: 'Minimal viable move' },
            { type: 'p', text: 'Write a one-page problem brief and pick the smallest AI component that moves one metric. Target: one workflow, one user group, one measurable change.' },
            { type: 'h2', text: '2. Over-engineering rare cases' },
            { type: 'p', text: "It's tempting to design for every possible exception. Teams spend weeks covering edge scenarios while the most frequent, routine steps stay untouched." },
            { type: 'p', text: "Think of the small actions: copying IDs, switching between dashboards, pasting updates. They may seem minor, but multiplied by hundreds of times per week, they drain hours." },
            { type: 'p', text: "The lesson: start with the tasks that happen most often. Automating small, repetitive actions usually saves more time than building complex solutions for rare edge cases." },
            { type: 'h2', text: '3. Forgetting about human behavior' },
            { type: 'p', text: "AI is not just a technical upgrade—it changes how people work. If the new process feels harder than the old one, adoption will stall." },
            { type: 'p', text: "The key is to design around behavior: place information exactly where it's needed. Offer pre-filled drafts that can be edited, not forced. Use small prompts at the right moment." },
            { type: 'p', text: 'When the helpful action becomes the easiest action, teams adopt AI without resistance.' },
            { type: 'h2', text: '4. Measuring the wrong things' },
            { type: 'p', text: 'Some projects focus on model accuracy or technical metrics. But customers and managers care about outcomes: faster responses, fewer repeat contacts, reduced refunds.' },
            { type: 'p', text: 'Instead of asking "Is the model 92% accurate?", ask: Did first response times improve? Did employees save measurable hours each week? Are customers contacting support less often?' },
            { type: 'p', text: 'The right metrics build trust, because they tie AI to real business improvements.' },
            { type: 'h2', text: '5. Launching too big, too soon' },
            { type: 'p', text: "Another mistake is rolling out everything at once. Big-bang launches often break in unexpected places, damaging trust and making teams hesitant to try again." },
            { type: 'p', text: "Smaller, reversible pilots work better. A two-week trial with a few people is enough to learn, adjust, and prove value. If something goes wrong, it can be switched off quickly." },
            { type: 'list', items: ['Pilot with a small group.', 'Collect feedback and adjust.', 'Expand gradually with safeguards.'] },
            { type: 'p', text: 'This way, AI feels like a reliable assistant rather than a risky experiment.' },
            { type: 'h2', text: 'Closing thoughts' },
            { type: 'p', text: "AI adoption isn't about finding the smartest model—it's about solving the right problems in the right order. Start small, automate routine work first, design around human behavior, measure meaningful outcomes, and scale gradually. Do this, and AI becomes less of a buzzword and more of a quiet advantage in everyday business." },
        ],
    },
    {
        id: '2',
        title: 'How to Spot AI Automation Opportunities in Your Workflow',
        date: 'September 18, 2025',
        image: 'https://framerusercontent.com/images/8sYyKjze9DvubaHdtzGBklijw.jpg?width=668&height=687',
        author: {
            name: 'Sarah Mitchell',
            role: 'AI Strategy Consultant',
            avatar: 'https://framerusercontent.com/images/dE1M3PnWabeUQOIzkbxSYm9EjvE.jpg?width=63&height=63',
        },
        subtitle: 'Finding the right moments to apply automation is a skill in itself — one that separates teams that thrive from those that struggle.',
        content: [
            { type: 'intro', text: "Most organizations have more automation potential than they realize. The challenge isn't finding AI tools — it's identifying where they'll actually create value." },
            { type: 'h2', text: 'Start with repetition' },
            { type: 'p', text: 'The easiest wins are tasks done the same way, every time. If a team member can write a checklist for it, a system can likely handle it.' },
            { type: 'h2', text: 'Look for handoff friction' },
            { type: 'p', text: 'Anywhere information moves between people or systems is a candidate for automation. Copy-paste, manual data entry, status update emails — all of these are signals.' },
            { type: 'h2', text: 'Map the pain points' },
            { type: 'p', text: "Talk to the people doing the work. Ask: what takes the most time? What do you dread each morning? Their answers will surface automation opportunities faster than any audit." },
        ],
    },
    {
        id: '3',
        title: 'How a Small Retail Business Saved 50+ Hours per Month with AI',
        date: 'September 3, 2025',
        image: 'https://framerusercontent.com/images/03R76oStnhKNMY4bxeygKssJa30.jpg?width=666&height=933',
        author: {
            name: 'Sarah Mitchell',
            role: 'AI Strategy Consultant',
            avatar: 'https://framerusercontent.com/images/dE1M3PnWabeUQOIzkbxSYm9EjvE.jpg?width=63&height=63',
        },
        subtitle: 'A case study in practical, low-cost AI implementation that delivered measurable results in under 30 days.',
        content: [
            { type: 'intro', text: "When a small retailer came to us overwhelmed by manual inventory checks and customer follow-ups, we didn't pitch a big platform. We started with two automations." },
            { type: 'h2', text: 'The problem' },
            { type: 'p', text: 'The team was spending 3+ hours daily copying order data between systems and manually sending restock alerts. It was reliable but exhausting.' },
            { type: 'h2', text: 'The solution' },
            { type: 'p', text: 'We connected their inventory system to a simple AI classifier that flagged restock needs automatically, and built a one-click email draft for follow-ups. Total setup: four days.' },
            { type: 'h2', text: 'The result' },
            { type: 'p', text: '52 hours saved in the first month. Staff morale improved. And the owner finally had time to focus on buying decisions instead of data entry.' },
        ],
    },
    {
        id: '4',
        title: 'Top 10 AI Tools Every Business Should Consider',
        date: 'August 6, 2025',
        image: 'https://framerusercontent.com/images/cP1GNLLWaB10MRp9jKnjWjsZA.jpg?width=669&height=450',
        author: {
            name: 'Sarah Mitchell',
            role: 'AI Strategy Consultant',
            avatar: 'https://framerusercontent.com/images/dE1M3PnWabeUQOIzkbxSYm9EjvE.jpg?width=63&height=63',
        },
        subtitle: 'Not every AI tool is worth your time. Here are the ones that consistently deliver ROI across business sizes and industries.',
        content: [
            { type: 'intro', text: 'The AI tools landscape is crowded. We cut through the noise and tested dozens of tools across real business scenarios to find what actually works.' },
            { type: 'h2', text: 'For writing and content' },
            { type: 'p', text: 'Tools like Claude and GPT-4 have become indispensable for drafting, editing, and summarizing. The key is giving them enough context and reviewing every output.' },
            { type: 'h2', text: 'For automation' },
            { type: 'p', text: 'Make and Zapier remain the gold standard for connecting apps. Pair them with an AI step and you have a powerful, low-code automation stack.' },
            { type: 'h2', text: 'For analytics' },
            { type: 'p', text: "Tools that interpret data in plain language let non-technical teams answer their own questions without waiting on analysts." },
        ],
    },
];

function RenderContent({ blocks }) {
    return (
        <div>
            {blocks.map((block, i) => {
                if (block.type === 'intro') return <p key={i} className="ap-intro">{block.text}</p>;
                if (block.type === 'h2') return <h2 key={i} className="ap-h2">{block.text}</h2>;
                if (block.type === 'h3') return <h3 key={i} className="ap-h3">{block.text}</h3>;
                if (block.type === 'p') return <p key={i} className="ap-p">{block.text}</p>;
                if (block.type === 'list') return (
                    <ul key={i} className="ap-list">
                        {block.items.map((item, j) => <li key={j}>{item}</li>)}
                    </ul>
                );
                return null;
            })}
        </div>
    );
}

/* ── Typewriter card (More Articles grid) ── */
function TypewriterCard({ a }) {
    const [displayed, setDisplayed] = useState(a.title);
    const [typing, setTyping] = useState(false);
    const timerRef = useRef(null);
    const indexRef = useRef(a.title.length);

    const startType = useCallback(() => {
        clearTimeout(timerRef.current);
        setTyping(true); setDisplayed(''); indexRef.current = 0;
        const type = () => {
            indexRef.current += 1;
            setDisplayed(a.title.slice(0, indexRef.current));
            if (indexRef.current < a.title.length) timerRef.current = setTimeout(type, 26);
            else setTyping(false);
        };
        timerRef.current = setTimeout(type, 26);
    }, [a.title]);

    const stopType = useCallback(() => {
        clearTimeout(timerRef.current);
        setTyping(false); setDisplayed(a.title); indexRef.current = a.title.length;
    }, [a.title]);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    return (
        <Link to={`/news/${a.id}`} className="ns-card" onMouseEnter={startType} onMouseLeave={stopType}>
            <p className="ns-date">{a.date}</p>
            <div className="ns-img-wrap">
                <img src={a.image} alt={a.title} className="ns-img" loading="lazy" />
            </div>
            <h3 className="ns-title">
                {displayed}
                {typing && <span className="ns-cursor">|</span>}
            </h3>
        </Link>
    );
}

/* ── NAVBAR HEIGHT used for sticky offset ──
   Change NAVBAR_H to match your actual Navbar height (px) */
const NAVBAR_H = 64;

export default function ArticlePage() {
    const { id } = useParams();
    const article = ALL_ARTICLES.find(a => a.id === id) || ALL_ARTICLES[0];
    const currentIdx = ALL_ARTICLES.findIndex(a => a.id === article.id);
    const nextArticle = ALL_ARTICLES[currentIdx + 1] || null;
    const moreArticles = ALL_ARTICLES.filter(a => a.id !== article.id).slice(0, 4);

    useEffect(() => { window.scrollTo(0, 0); }, [id]);

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ══ PAGE ══ */
        .ap-page {
          background: #0a0a0a;
          color: #f0f0f0;
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          position: relative;
        }
        /* noise overlay */
        .ap-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background: url('https://www.transparenttextures.com/patterns/asfalt-dark.png') repeat;
          opacity: 0.4;
          pointer-events: none;
          z-index: 0;
        }
        .ap-page > * { position: relative; z-index: 1; }

        /* ══ THREE-COLUMN LAYOUT
           [line] [sidebar 340px] [line] [gap ~420px] [line] [article 1fr]
        ══ */
        .ap-layout {
          display: grid;
          grid-template-columns: 1px 340px 1px 420px 1px 1fr;
          align-items: start;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        /* ── vertical divider lines ── */
        .ap-vline {
          background: rgba(255,255,255,0.07);
          align-self: stretch;
          width: 1px;
          min-height: 100%;
        }

        /* ── COL 2 — Sticky sidebar ── */
        .ap-sidebar {
          position: sticky;
          top: ${NAVBAR_H}px;
          height: calc(100vh - ${NAVBAR_H}px);
          display: flex;
          flex-direction: column;
          padding: 40px 32px;
          gap: 0;
          overflow: hidden;
        }

        /* Square 1:1 image */
        .ap-sidebar-img {
          width: 100%;
          aspect-ratio: 1 / 1;
          object-fit: cover;
          object-position: top center;
          display: block;
        }

        /* Author row below image */
        .ap-author {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 22px;
        }
        .ap-author-avatar {
          width: 36px; height: 36px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          border: 1.5px solid rgba(255,255,255,0.15);
        }
        .ap-author-name {
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          color: #fff;
          line-height: 1;
        }
        .ap-author-role {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.54rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-top: 5px;
        }

        /* ── empty spacer gap ── */
        .ap-gap {
          /* intentionally empty — provides the wide center gap */
        }

        /* ── scrollable article ── */
        .ap-article {
          padding: 48px 80px 120px 48px;
          min-height: 100vh;
        }

        /* date */
        .ap-meta-date {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.6rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-bottom: 24px;
        }
        .ap-meta-date::before {
          content: '';
          width: 5px; height: 5px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          flex-shrink: 0;
        }

        /* Giant headline */
        .ap-headline {
          font-size: clamp(2.4rem, 5vw, 5rem);
          font-weight: 800;
          line-height: 0.95;
          letter-spacing: -0.04em;
          text-transform: uppercase;
          color: #fff;
          margin-bottom: 36px;
        }

        /* Subtitle */
        .ap-subtitle {
          border-left: 2px solid rgba(255,255,255,0.14);
          padding: 4px 0 4px 20px;
          margin-bottom: 44px;
          font-size: 0.94rem;
          font-style: normal;
          color: rgba(255,255,255,0.38);
          line-height: 1.75;
        }

        /* Horizontal rule */
        .ap-rule {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 52px;
        }

        /* ── Body copy ── */
        .ap-intro {
          font-size: 1.06rem;
          font-weight: 500;
          line-height: 1.9;
          color: rgba(255,255,255,0.82);
          margin-bottom: 32px;
        }
        .ap-p {
          font-size: 0.96rem;
          line-height: 1.95;
          color: rgba(255,255,255,0.5);
          margin-bottom: 24px;
        }
        .ap-h2 {
          font-size: 1.28rem;
          font-weight: 700;
          letter-spacing: -0.025em;
          color: #fff;
          margin: 56px 0 16px;
          line-height: 1.22;
        }
        .ap-h3 {
          font-size: 0.875rem;
          font-weight: 600;
          color: rgba(255,255,255,0.45);
          margin: 28px 0 10px;
          letter-spacing: 0.01em;
        }
        .ap-list {
          list-style: none;
          padding: 0;
          margin: 0 0 24px;
        }
        .ap-list li {
          font-size: 0.94rem;
          line-height: 1.9;
          color: rgba(255,255,255,0.48);
          padding-left: 22px;
          position: relative;
          margin-bottom: 8px;
        }
        .ap-list li::before {
          content: '•';
          position: absolute;
          left: 6px;
          color: rgba(255,255,255,0.25);
          font-size: 0.7rem;
          top: 0.18em;
        }

        /* ── Next article card ── */
        .ap-next {
          display: flex;
          align-items: stretch;
          text-decoration: none;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          overflow: hidden;
          margin-top: 64px;
          background: rgba(255,255,255,0.04);
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .ap-next:hover {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.18);
          transform: translateY(-2px);
        }
        .ap-next-img {
          width: 80px; height: 80px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .ap-next-body {
          padding: 14px 20px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 6px;
        }
        .ap-next-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.54rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.3);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ap-next-label::before {
          content: '';
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(255,255,255,0.3);
        }
        .ap-next-title {
          font-size: 0.9rem;
          font-weight: 500;
          color: #fff;
          line-height: 1.42;
        }

        /* ── Social share ── */
        .ap-social {
          display: flex;
          gap: 8px;
          margin-top: 40px;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.09);
        }
        .ap-social-btn {
          width: 36px; height: 36px;
          border-radius: 50%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          color: rgba(255,255,255,0.4);
          font-size: 0.65rem;
          font-weight: 700;
          transition: background 0.18s, color 0.18s, border-color 0.18s;
          font-family: 'Inter', sans-serif;
        }
        .ap-social-btn:hover { background: #fff; color: #111; border-color: #fff; }

        /* ══ MORE ARTICLES ══ */
        .ns-section {
          position: relative;
          background: #0a0a0a;
          font-family: 'Inter', sans-serif;
          border-top: 1px solid rgba(255,255,255,0.07);
        }
        .ns-header {
          padding: clamp(40px,5vw,72px) clamp(20px,4vw,52px) clamp(24px,3vw,40px);
          border-bottom: 1px solid rgba(255,255,255,0.07);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 20px;
        }
        .ns-label {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.6rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.28);
          margin: 0 0 8px;
        }
        .ns-heading {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          font-weight: 700;
          color: #fff;
          margin: 0;
          letter-spacing: -0.035em;
          line-height: 1.1;
        }
        .ns-all-link {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.58rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          color: rgba(255,255,255,0.3);
          border-bottom: 1px solid rgba(255,255,255,0.15);
          padding-bottom: 2px;
          white-space: nowrap;
          transition: color 0.2s, border-color 0.2s;
          flex-shrink: 0;
          align-self: flex-end;
        }
        .ns-all-link:hover { color: #fff; border-color: #fff; }

        .ns-grid-wrap { border-bottom: 1px solid rgba(255,255,255,0.07); }
        .ns-grid { display: grid; grid-template-columns: repeat(4, 1fr); }

        .ns-card {
          display: flex;
          flex-direction: column;
          text-decoration: none;
          color: inherit;
          border-right: 1px solid rgba(255,255,255,0.07);
          padding: clamp(20px,2.5vw,32px) clamp(18px,2.2vw,30px) clamp(28px,3vw,44px);
          transition: background 0.25s;
          overflow: hidden;
        }
        .ns-card:first-child { border-left: 1px solid rgba(255,255,255,0.07); }
        .ns-card:hover { background: rgba(255,255,255,0.03); }

        .ns-date {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.6rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #e03030;
          margin: 0 0 14px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ns-date::before {
          content: '';
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #e03030;
          flex-shrink: 0;
        }
        .ns-img-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 2px;
          margin-bottom: 18px;
        }
        .ns-img {
          width: 100%;
          display: block;
          object-fit: cover;
          transform: scale(1);
          transition: transform 0.55s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .ns-card:hover .ns-img { transform: scale(1.05); }

        .ns-title {
          font-size: clamp(0.875rem,1.2vw,1.05rem);
          font-weight: 500;
          color: #fff;
          margin: 0;
          line-height: 1.52;
          letter-spacing: -0.01em;
          min-height: 3em;
        }

        @keyframes ns-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        .ns-cursor {
          display: inline-block;
          color: #e03030;
          font-weight: 400;
          margin-left: 1px;
          animation: ns-blink 0.6s step-start infinite;
        }

        /* ══════════════════════════════
           RESPONSIVE
        ══════════════════════════════ */

        /* Large desktop — slightly less gap */
        @media (max-width: 1440px) {
          .ap-layout { grid-template-columns: 1px 320px 1px 340px 1px 1fr; }
        }

        @media (max-width: 1280px) {
          .ap-layout { grid-template-columns: 1px 300px 1px 260px 1px 1fr; }
          .ap-article { padding: 44px 60px 100px 44px; }
        }

        /* Tablet landscape — narrower gap */
        @media (max-width: 1024px) {
          .ap-layout { grid-template-columns: 1px 260px 1px 160px 1px 1fr; }
          .ap-article { padding: 40px 48px 80px 40px; }
          .ap-headline { font-size: clamp(2rem, 5vw, 3.2rem); }
        }

        /* Tablet portrait — stack layout, keep lines on sides */
        @media (max-width: 860px) {
          .ap-layout {
            display: flex;
            flex-direction: column;
            position: relative;
          }
          /* left + right vertical lines on sides via pseudo-elements */
          .ap-layout::before,
          .ap-layout::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            width: 1px;
            background: rgba(255,255,255,0.07);
            z-index: 2;
          }
          .ap-layout::before { left: 16px; }
          .ap-layout::after  { right: 16px; }

          .ap-vline, .ap-gap { display: none; }

          /* Sidebar stacks vertically: author on top, image below (full width) */
          .ap-sidebar {
            position: relative;
            top: auto;
            height: auto;
            flex-direction: column-reverse;
            align-items: flex-start;
            gap: 0;
            padding: 0 36px;
            border-bottom: none;
          }
          /* Author row — sits at top */
          .ap-author {
            padding: 24px 0 20px;
            order: 2;
          }
          /* Full-width image below author */
          .ap-sidebar-img {
            width: 100%;
            height: auto;
            aspect-ratio: 4 / 3;
            flex-shrink: 0;
            order: 1;
          }

          .ap-article {
            padding: 44px 36px 72px;
            max-width: 100%;
          }
          .ap-headline { font-size: clamp(1.9rem, 7vw, 2.8rem); }

          .ns-grid { grid-template-columns: repeat(2, 1fr); }
          .ns-card:nth-child(2) { border-right: none; }
          .ns-card:nth-child(odd) { border-left: 1px solid rgba(255,255,255,0.07); }
          .ns-card:nth-child(3),
          .ns-card:nth-child(4) { border-top: 1px solid rgba(255,255,255,0.07); }
        }

        /* Mobile */
        @media (max-width: 560px) {
          .ap-layout::before { left: 10px; }
          .ap-layout::after  { right: 10px; }

          .ap-sidebar { padding: 0 24px; }
          .ap-author { padding: 18px 0 16px; }
          .ap-sidebar-img { aspect-ratio: 4 / 3; }

          .ap-article { padding: 36px 24px 60px; }
          .ap-headline { font-size: clamp(1.65rem, 8vw, 2.2rem); }
          .ap-h2 { font-size: 1.1rem; margin: 40px 0 14px; }
          .ap-p, .ap-intro { font-size: 0.9rem; }
          .ap-subtitle { font-size: 0.875rem; }

          .ns-grid { grid-template-columns: 1fr; }
          .ns-card {
            border-right: none !important;
            border-left: none !important;
            border-top: 1px solid rgba(255,255,255,0.07);
          }
          .ns-card:first-child { border-top: none; }
          .ns-header {
            padding: 36px 16px 22px;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
        }
      `}</style>

            <div className="ap-page">
                <Navbar />

                {/* ══ ARTICLE — 6-column grid ══ */}
                <div className="ap-layout">

                    {/* COL 1 — Leftmost vertical line */}
                    <div className="ap-vline ap-vline-left" aria-hidden="true" />

                    {/* COL 2 — Sticky sidebar */}
                    <aside className="ap-sidebar">
                        <img
                            src={article.image}
                            alt={article.title}
                            className="ap-sidebar-img"
                        />
                        <div className="ap-author">
                            <img
                                src={article.author.avatar}
                                alt={article.author.name}
                                className="ap-author-avatar"
                            />
                            <div>
                                <div className="ap-author-name">{article.author.name}</div>
                                <div className="ap-author-role">{article.author.role}</div>
                            </div>
                        </div>
                    </aside>

                    {/* COL 3 — Middle-left vertical line */}
                    <div className="ap-vline" aria-hidden="true" />

                    {/* COL 4 — Empty gap between the two lines */}
                    <div className="ap-gap" aria-hidden="true" />

                    {/* COL 5 — Middle-right vertical line */}
                    <div className="ap-vline ap-vline-right" aria-hidden="true" />

                    {/* COL 6 — Scrollable article body */}
                    <article className="ap-article">
                        <p className="ap-meta-date">{article.date}</p>
                        <h1 className="ap-headline">{article.title}</h1>
                        <blockquote className="ap-subtitle">{article.subtitle}</blockquote>
                        <hr className="ap-rule" />

                        <RenderContent blocks={article.content} />

                        {nextArticle && (
                            <Link to={`/news/${nextArticle.id}`} className="ap-next">
                                <img src={nextArticle.image} alt={nextArticle.title} className="ap-next-img" />
                                <div className="ap-next-body">
                                    <span className="ap-next-label">Next Article</span>
                                    <span className="ap-next-title">{nextArticle.title}</span>
                                </div>
                            </Link>
                        )}

                        <div className="ap-social">
                            <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="ap-social-btn">𝕏</a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="ap-social-btn">IG</a>
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="ap-social-btn">in</a>
                        </div>
                    </article>
                    <div className="ap-vline ap-vline-extra-left" aria-hidden="true" />
                    <div className="ap-vline ap-vline-extra-right" aria-hidden="true" />
                </div>

                {/* ══ MORE ARTICLES ══ */}
                <section className="ns-section">
                    <div className="ns-header">
                        <div>
                            <p className="ns-label">about AI for business</p>
                            <h2 className="ns-heading">More articles</h2>
                        </div>
                        <Link to="/news" className="ns-all-link">All articles →</Link>
                    </div>
                    <div className="ns-grid-wrap">
                        <div className="ns-grid">
                            {moreArticles.map((a) => (
                                <TypewriterCard key={a.id} a={a} />
                            ))}
                        </div>
                    </div>
                </section>

                <Footer />
            </div>
        </>
    );
}